// Function to dynamically import all members from their individual folders

/**
 * Gets all member folders and loads their data
 * @returns {Promise<Array>} Array of member objects
 */
export async function getAllMembers() {
  try {
    // Dynamically fetch the list of member folders
    let memberFolders = [];

    try {
      // First, try to fetch a directory index if it exists
      const directoryResponse = await fetch('/members/directory.json');
      if (directoryResponse.ok) {
        const directoryData = await directoryResponse.json();
        memberFolders = directoryData.folders;
      } else {
        // Fallback: scan the members directory by trying to fetch a manifest
        console.log(
          'No directory.json found, attempting to scan members directory'
        );
        const manifestResponse = await fetch('/members/manifest.json');
        if (manifestResponse.ok) {
          const manifestData = await manifestResponse.json();
          memberFolders = manifestData.members;
        }
      }
    } catch (error) {
      console.error('Error fetching member directory:', error);
      // Fallback to minimal hardcoded list if all else fails
      memberFolders = ['Aya', 'Akai'];
      console.warn('Using fallback member list due to directory fetch error');
    }

    // Load each member's data
    const memberPromises = memberFolders.map((folderId) =>
      fetch(`/members/${folderId}/data.json`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to load member data for ${folderId}`);
          }
          return response.json();
        })
        .then((data) => {
          // Add the folder id as the member id if not present
          return { id: folderId.toLowerCase(), ...data };
        })
        .catch((error) => {
          console.error(`Error loading member ${folderId}:`, error);
          return null;
        })
    );

    // Wait for all member data to be loaded
    const members = await Promise.all(memberPromises);

    // Filter out any null entries (failed loads)
    return members.filter((member) => member !== null);
  } catch (error) {
    console.error('Error loading members:', error);
    return [];
  }
}

/**
 * Gets a specific member by ID
 * @param {string} memberId - The ID of the member to get
 * @returns {Promise<Object|null>} The member object or null if not found
 */
export async function getMemberById(memberId) {
  try {
    // Find the correct capitalization of the folder name
    const response = await fetch(`/members/${memberId}/data.json`);

    if (!response.ok) {
      // Try with first letter capitalized
      const capitalizedId =
        memberId.charAt(0).toUpperCase() + memberId.slice(1);
      const secondResponse = await fetch(`/members/${capitalizedId}/data.json`);

      if (!secondResponse.ok) {
        throw new Error(`Member ${memberId} not found`);
      }

      const data = await secondResponse.json();
      return { id: memberId, ...data };
    }

    const data = await response.json();
    return { id: memberId, ...data };
  } catch (error) {
    console.error(`Error getting member ${memberId}:`, error);
    return null;
  }
}
